import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";

import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

import {
    Alert,
    Badge,
    Button,
    Container,
    Divider,
    Grid,
    Group,
    Loader,
    Paper,
    ScrollArea,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";

import {
    IconBeer,
    IconBottle,
    IconDownload,
    IconFilter,
    IconGlass,
    IconReceipt,
    IconRefresh,
} from "@tabler/icons-react";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkOverviewDto {
    name: string;
    collectionName?: string;
    date: string;
    bar: number;
    beer: number;
    water: number;
    wine: number;
    total: number;
}

interface EventBar {
    eventBarId: number;
    name: string;
    isActive: boolean;
}

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.value)) return result.value;

    return [];
};

const toNumber = (
    value: any,
    fallback = 0
): number => {
    const num = Number(value);

    return Number.isFinite(num)
        ? num
        : fallback;
};

const toSafeDateString = (
    value: any
): string => {
    if (!value) return "";

    const str = String(value);

    return str.length >= 10
        ? str.substring(0, 10)
        : str;
};

const formatDisplayDate = (
    value: any
): string => {
    const safeDate =
        toSafeDateString(value);

    if (!safeDate) return "-";

    const parts =
        safeDate.split("-");

    if (parts.length !== 3) {
        return safeDate;
    }

    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const deriveCollectionName = (
    row: any,
    resolvedName: string
): string => {
    const explicit =
        row?.collectionName ??
        row?.CollectionName ??
        row?.ticketCollectionName ??
        row?.TicketCollectionName;

    if (
        explicit &&
        String(explicit).trim()
    ) {
        return String(
            explicit
        ).trim();
    }

    const name =
        (resolvedName || "").trim();

    if (name.includes(" #")) {
        return name
            .split(" #")[0]
            .trim();
    }

    return "";
};

const normalizeOverviewRow = (
    row: any
): DrinkOverviewDto => {
    const resolvedName =
        row?.name ??
        row?.Name ??
        row?.personName ??
        row?.PersonName ??
        row?.ticketName ??
        row?.TicketName ??
        "";

    return {
        name: String(
            resolvedName || ""
        ).trim(),

        collectionName:
            deriveCollectionName(
                row,
                resolvedName
            ),

        date:
            row?.date ??
            row?.Date ??
            row?.createdAt ??
            row?.CreatedAt ??
            "",

        bar: toNumber(
            row?.bar ??
            row?.Bar
        ),

        beer: toNumber(
            row?.beer ??
            row?.Beer
        ),

        water: toNumber(
            row?.water ??
            row?.Water
        ),

        wine: toNumber(
            row?.wine ??
            row?.Wine
        ),

        total: toNumber(
            row?.total ??
            row?.Total
        ),
    };
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const AdminDrinkOverview: React.FC = () => {
    const apiUrl =
        process.env.REACT_APP_API_URL || "";

    // -----------------------------------------------------
    // STATE
    // -----------------------------------------------------

    const [events, setEvents] =
        useState<EventDto[]>([]);

    const [eventId, setEventId] =
        useState<number>(0);

    const [overview, setOverview] =
        useState<DrinkOverviewDto[]>([]);

    const [bars, setBars] =
        useState<EventBar[]>([]);

    const [
        selectedDate,
        setSelectedDate,
    ] =
        useState<string | null>(
            null
        );

    const [
        selectedBar,
        setSelectedBar,
    ] =
        useState<string | null>(
            null
        );

    const [
        selectedName,
        setSelectedName,
    ] =
        useState<string | null>(
            null
        );

    const [
        selectedCollectionName,
        setSelectedCollectionName,
    ] =
        useState<string | null>(
            null
        );

    const [
        availableDates,
        setAvailableDates,
    ] =
        useState<string[]>([]);


    const [
        availableNames,
        setAvailableNames,
    ] =
        useState<string[]>([]);

    const [
        availableCollectionNames,
        setAvailableCollectionNames,
    ] =
        useState<string[]>([]);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState<string | null>(
            null
        );

    // -----------------------------------------------------
    // EVENTS
    // -----------------------------------------------------

    useEffect(() => {
        const loadEvents =
            async () => {
                try {
                    const res =
                        await fetch(
                            `${apiUrl}/api/events`
                        );

                    if (!res.ok) {
                        return;
                    }

                    const data =
                        await res.json();

                    setEvents(
                        toArray<EventDto>(
                            data
                        )
                    );
                } catch (err) {
                    console.error(
                        err
                    );
                }
            };

        loadEvents();
    }, [apiUrl]);

    // -----------------------------------------------------
    // BARS
    // -----------------------------------------------------

    const loadBars =
        async (
            currentEventId: number
        ) => {
            try {
                const res =
                    await fetch(
                        `${apiUrl}/api/EventBars/event/${currentEventId}`
                    );

                if (!res.ok) {
                    setBars([]);
                    return;
                }

                const data =
                    await res.json();

                setBars(
                    toArray<EventBar>(
                        data
                    )
                );
            } catch (err) {
                console.error(
                    err
                );

                setBars([]);
            }
        };

    const getBarName = (
        barId: number
    ) => {
        return (
            bars.find(
                (bar) =>
                    bar.eventBarId ===
                    barId
            )?.name ??
            `Bar ${barId}`
        );
    };

    // -----------------------------------------------------
    // OVERVIEW
    // -----------------------------------------------------

    const fetchOverview =
        async (
            event: number
        ) => {
            if (!event) return;

            try {
                setLoading(true);
                setError(null);

                const res =
                    await fetch(
                        `${apiUrl}/api/drinks/overview?eventId=${event}`
                    );

                if (!res.ok) {
                    throw new Error(
                        await res.text()
                    );
                }

                const raw =
                    await res.json();

                const rows =
                    toArray<any>(
                        raw
                    ).map(
                        normalizeOverviewRow
                    );

                setOverview(
                    rows
                );

                setAvailableDates(Array.from(
                        new Set(
                            rows
                                .map(
                                    (
                                        row
                                    ) =>
                                        toSafeDateString(
                                            row.date
                                        )
                                )
                                .filter(
                                    Boolean
                                )
                        )
                    ).sort()
                );


                setAvailableNames(
                    Array.from(
                        new Set(
                            rows
                                .map(
                                    (
                                        row
                                    ) =>
                                        row.name
                                )
                                .filter(
                                    Boolean
                                )
                        )
                    ).sort()
                );

                setAvailableCollectionNames(
                    Array.from(
                        new Set(
                            rows
                                .map(
                                    (
                                        row
                                    ) =>
                                        row.collectionName ||
                                        ""
                                )
                                .filter(
                                    Boolean
                                )
                        )
                    ).sort()
                );
            } catch (
            err: any
            ) {
                setError(
                    err?.message ||
                    "Could not load overview."
                );

                setOverview(
                    []
                );
            } finally {
                setLoading(
                    false
                );
            }
        };

    // -----------------------------------------------------
    // EVENT CHANGE
    // -----------------------------------------------------

    useEffect(() => {
        setSelectedDate(
            null
        );

        setSelectedBar(
            null
        );

        setSelectedName(
            null
        );

        setSelectedCollectionName(
            null
        );

        setOverview([]);
        setBars([]);

        if (!eventId) {
            return;
        }

        fetchOverview(
            eventId
        );

        loadBars(
            eventId
        );
    }, [eventId]);

    // -----------------------------------------------------
    // FILTER
    // -----------------------------------------------------

    const filteredOverview =
        useMemo(() => {
            return overview.filter(
                (row) => {
                    const rowDate =
                        toSafeDateString(
                            row.date
                        );

                    return (
                        (!selectedDate ||
                            rowDate ===
                            selectedDate) &&
                        (!selectedBar ||
                            row.bar ===
                            Number(
                                selectedBar
                            )) &&
                        (!selectedName ||
                            row.name ===
                            selectedName) &&
                        (!selectedCollectionName ||
                            row.collectionName ===
                            selectedCollectionName)
                    );
                }
            );
        }, [
            overview,
            selectedDate,
            selectedBar,
            selectedName,
            selectedCollectionName,
        ]);

    // -----------------------------------------------------
    // TOTALS
    // -----------------------------------------------------

    const totals =
        useMemo(() => {
            return filteredOverview.reduce(
                (
                    acc,
                    row
                ) => {
                    acc.beer +=
                        toNumber(
                            row.beer
                        );

                    acc.water +=
                        toNumber(
                            row.water
                        );

                    acc.wine +=
                        toNumber(
                            row.wine
                        );

                    acc.total +=
                        toNumber(
                            row.total
                        );

                    return acc;
                },
                {
                    beer: 0,
                    water: 0,
                    wine: 0,
                    total: 0,
                }
            );
        }, [
            filteredOverview,
        ]);

    // -----------------------------------------------------
    // FILTER COUNT
    // -----------------------------------------------------

    const activeFilterCount =
        [
            selectedDate,
            selectedBar,
            selectedName,
            selectedCollectionName,
        ].filter(
            Boolean
        ).length;

    // -----------------------------------------------------
    // RESET FILTERS
    // -----------------------------------------------------

    const resetFilters =
        () => {
            setSelectedDate(
                null
            );

            setSelectedBar(
                null
            );

            setSelectedName(
                null
            );

            setSelectedCollectionName(
                null
            );
        };

    // -----------------------------------------------------
    // EXPORT
    // -----------------------------------------------------

    const exportToExcel =
        () => {
            const exportRows =
                filteredOverview.map(
                    (row) => ({
                        Navn:
                            row.name,

                        Collection:
                            row.collectionName ||
                            "",

                        Dato:
                            toSafeDateString(
                                row.date
                            ),

                        Bar:
                            getBarName(
                                row.bar
                            ),

                        Øl:
                            row.beer,

                        Vand:
                            row.water,

                        Vin:
                            row.wine,

                        Total:
                            row.total,
                    })
                );

            const worksheet =
                XLSX.utils.json_to_sheet(
                    exportRows
                );

            const workbook =
                XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Drink Overview"
            );

            XLSX.writeFile(
                workbook,
                `drink_overview_${eventId}_${new Date()
                    .toISOString()
                    .slice(
                        0,
                        10
                    )}.xlsx`
            );
        };

    // -----------------------------------------------------
    // RENDER
    // -----------------------------------------------------

    return (
        <div>
            {/* HERO */}

            <HeroSectionV2
                titleEn="Bar Overview"
                titleDa="Bar Oversigt"
                descriptionEn="View consumption by person, date, bar, and collection"
                descriptionDa="Se forbrug pr. person, dato, bar og collection"
                imageSrc={
                    Billede1
                }
            />

            <AdminBackBar
                title="Drinks"
                crumbs={[
                    {
                        label:
                            "Admin",
                        to:
                            "/admin",
                    },
                    {
                        label:
                            "Drinks",
                    },
                ]}
            />

            <Container
                size="xl"
                py="xl"
            >
                <Stack gap="xl">

                    {/* ===================================== */}
                    {/* ERROR */}
                    {/* ===================================== */}

                    {error && (
                        <Alert
                            color="red"
                            title="Error"
                            withCloseButton
                            onClose={() =>
                                setError(
                                    null
                                )
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* ===================================== */}
                    {/* FILTERS */}
                    {/* ===================================== */}

                    <Paper
                        withBorder
                        radius="lg"
                        shadow="xs"
                        style={{
                            overflow:
                                "hidden",
                        }}
                    >
                        <Group
                            justify="space-between"
                            p="lg"
                        >
                            <Group gap="sm">
                                <ThemeIcon
                                    color="red"
                                    variant="light"
                                    size="lg"
                                    radius="md"
                                >
                                    <IconFilter
                                        size={
                                            19
                                        }
                                    />
                                </ThemeIcon>

                                <div>
                                    <Group gap="xs">
                                        <Title
                                            order={
                                                4
                                            }
                                        >
                                            Filters
                                        </Title>

                                        {activeFilterCount >
                                            0 && (
                                                <Badge
                                                    color="red"
                                                    variant="light"
                                                >
                                                    {
                                                        activeFilterCount
                                                    }
                                                </Badge>
                                            )}
                                    </Group>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Select an event and filter the consumption data
                                    </Text>
                                </div>
                            </Group>

                            {activeFilterCount >
                                0 && (
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        size="xs"
                                        leftSection={
                                            <IconRefresh
                                                size={
                                                    15
                                                }
                                            />
                                        }
                                        onClick={
                                            resetFilters
                                        }
                                    >
                                        Reset filters
                                    </Button>
                                )}
                        </Group>

                        <Divider />

                        <Stack
                            p="lg"
                            gap="md"
                        >
                            {/* EVENT */}

                            <Grid>
                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6,
                                    }}
                                >
                                    <Select
                                        label="Event"
                                        placeholder="Select event..."
                                        value={
                                            eventId
                                                ? String(
                                                    eventId
                                                )
                                                : null
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            setEventId(
                                                value
                                                    ? Number(
                                                        value
                                                    )
                                                    : 0
                                            )
                                        }
                                        data={events.map(
                                            (
                                                event
                                            ) => ({
                                                value:
                                                    String(
                                                        event.eventID
                                                    ),

                                                label:
                                                    event.eventName ||
                                                    `Event ${event.eventID}`,
                                            })
                                        )}
                                        searchable
                                        clearable
                                    />
                                </Grid.Col>

                                {/* BAR */}

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6,
                                    }}
                                >
                                    <Select
                                        label="Bar"
                                        placeholder={
                                            eventId
                                                ? "All bars"
                                                : "Select event first..."
                                        }
                                        disabled={!eventId}
                                        clearable
                                        searchable
                                        value={selectedBar}
                                        onChange={setSelectedBar}
                                        data={bars
                                            .slice()
                                            .sort((a, b) => a.eventBarId - b.eventBarId)
                                            .map((bar) => ({
                                                value: String(bar.eventBarId),
                                                label: bar.name,
                                            }))}
                                    />
                                </Grid.Col>
                            </Grid>

                            {/* EXTRA FILTERS */}

                            {eventId >
                                0 && (
                                    <Grid>
                                        <Grid.Col
                                            span={{
                                                base: 12,
                                                sm: 6,
                                                lg: 4,
                                            }}
                                        >
                                            <Select
                                                label="Date"
                                                placeholder="All dates"
                                                clearable
                                                searchable
                                                value={
                                                    selectedDate
                                                }
                                                onChange={
                                                    setSelectedDate
                                                }
                                                data={availableDates.map(
                                                    (
                                                        date
                                                    ) => ({
                                                        value:
                                                            date,

                                                        label:
                                                            formatDisplayDate(
                                                                date
                                                            ),
                                                    })
                                                )}
                                            />
                                        </Grid.Col>

                                        <Grid.Col
                                            span={{
                                                base: 12,
                                                sm: 6,
                                                lg: 4,
                                            }}
                                        >
                                            <Select
                                                label="Name"
                                                placeholder="All names"
                                                clearable
                                                searchable
                                                value={
                                                    selectedName
                                                }
                                                onChange={
                                                    setSelectedName
                                                }
                                                data={availableNames.map(
                                                    (
                                                        name
                                                    ) => ({
                                                        value:
                                                            name,

                                                        label:
                                                            name,
                                                    })
                                                )}
                                            />
                                        </Grid.Col>

                                        <Grid.Col
                                            span={{
                                                base: 12,
                                                lg: 4,
                                            }}
                                        >
                                            <Select
                                                label="Collection"
                                                placeholder="All collections"
                                                clearable
                                                searchable
                                                value={
                                                    selectedCollectionName
                                                }
                                                onChange={
                                                    setSelectedCollectionName
                                                }
                                                data={availableCollectionNames.map(
                                                    (
                                                        name
                                                    ) => ({
                                                        value:
                                                            name,

                                                        label:
                                                            name,
                                                    })
                                                )}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                )}
                        </Stack>
                    </Paper>

                    {/* ===================================== */}
                    {/* LOADING */}
                    {/* ===================================== */}

                    {loading && (
                        <Paper
                            withBorder
                            radius="lg"
                            p={60}
                        >
                            <Stack
                                align="center"
                                gap="sm"
                            >
                                <Loader
                                    color="red"
                                    size="sm"
                                />

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Loading overview...
                                </Text>
                            </Stack>
                        </Paper>
                    )}

                    {/* ===================================== */}
                    {/* DATA */}
                    {/* ===================================== */}

                    {!loading &&
                        eventId >
                        0 &&
                        filteredOverview.length >
                        0 && (
                            <>
                                {/* ========================= */}
                                {/* TOTAL CARDS */}
                                {/* ========================= */}

                                <SimpleGrid
                                    cols={{
                                        base: 2,
                                        md: 4,
                                    }}
                                    spacing="md"
                                >
                                    {/* BEER */}

                                    <Paper
                                        withBorder
                                        radius="lg"
                                        p="lg"
                                    >
                                        <Group
                                            justify="space-between"
                                            align="flex-start"
                                        >
                                            <div>
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Beer
                                                </Text>

                                                <Text
                                                    fw={
                                                        800
                                                    }
                                                    size="xl"
                                                    mt={
                                                        4
                                                    }
                                                >
                                                    {
                                                        totals.beer
                                                    }
                                                </Text>
                                            </div>

                                            <ThemeIcon
                                                color="red"
                                                variant="light"
                                                size="lg"
                                                radius="md"
                                            >
                                                <IconBeer
                                                    size={
                                                        19
                                                    }
                                                />
                                            </ThemeIcon>
                                        </Group>
                                    </Paper>

                                    {/* WATER */}

                                    <Paper
                                        withBorder
                                        radius="lg"
                                        p="lg"
                                    >
                                        <Group
                                            justify="space-between"
                                            align="flex-start"
                                        >
                                            <div>
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Water
                                                </Text>

                                                <Text
                                                    fw={
                                                        800
                                                    }
                                                    size="xl"
                                                    mt={
                                                        4
                                                    }
                                                >
                                                    {
                                                        totals.water
                                                    }
                                                </Text>
                                            </div>

                                            <ThemeIcon
                                                color="red"
                                                variant="light"
                                                size="lg"
                                                radius="md"
                                            >
                                                <IconBottle
                                                    size={
                                                        19
                                                    }
                                                />
                                            </ThemeIcon>
                                        </Group>
                                    </Paper>

                                    {/* WINE */}

                                    <Paper
                                        withBorder
                                        radius="lg"
                                        p="lg"
                                    >
                                        <Group
                                            justify="space-between"
                                            align="flex-start"
                                        >
                                            <div>
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Wine
                                                </Text>

                                                <Text
                                                    fw={
                                                        800
                                                    }
                                                    size="xl"
                                                    mt={
                                                        4
                                                    }
                                                >
                                                    {
                                                        totals.wine
                                                    }
                                                </Text>
                                            </div>

                                            <ThemeIcon
                                                color="red"
                                                variant="light"
                                                size="lg"
                                                radius="md"
                                            >
                                                <IconGlass
                                                    size={
                                                        19
                                                    }
                                                />
                                            </ThemeIcon>
                                        </Group>
                                    </Paper>

                                    {/* TOTAL */}

                                    <Paper
                                        withBorder
                                        radius="lg"
                                        p="lg"
                                        style={{
                                            borderColor:
                                                "var(--mantine-color-red-3)",
                                        }}
                                    >
                                        <Group
                                            justify="space-between"
                                            align="flex-start"
                                        >
                                            <div>
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Total drinks
                                                </Text>

                                                <Text
                                                    fw={
                                                        800
                                                    }
                                                    size="xl"
                                                    mt={
                                                        4
                                                    }
                                                >
                                                    {
                                                        totals.total
                                                    }
                                                </Text>
                                            </div>

                                            <ThemeIcon
                                                color="red"
                                                variant="filled"
                                                size="lg"
                                                radius="md"
                                            >
                                                <IconReceipt
                                                    size={
                                                        19
                                                    }
                                                />
                                            </ThemeIcon>
                                        </Group>
                                    </Paper>
                                </SimpleGrid>

                                {/* ========================= */}
                                {/* OVERVIEW TABLE */}
                                {/* ========================= */}

                                <Paper
                                    withBorder
                                    radius="lg"
                                    shadow="xs"
                                    style={{
                                        overflow:
                                            "hidden",
                                    }}
                                >
                                    <Group
                                        justify="space-between"
                                        p="lg"
                                    >
                                        <Group gap="sm">
                                            <ThemeIcon
                                                color="red"
                                                variant="light"
                                                size="lg"
                                                radius="md"
                                            >
                                                <IconReceipt
                                                    size={
                                                        19
                                                    }
                                                />
                                            </ThemeIcon>

                                            <div>
                                                <Group gap="xs">
                                                    <Title
                                                        order={
                                                            4
                                                        }
                                                    >
                                                        Overview
                                                    </Title>

                                                    <Badge
                                                        color="gray"
                                                        variant="light"
                                                    >
                                                        {
                                                            filteredOverview.length
                                                        }{" "}
                                                        rows
                                                    </Badge>
                                                </Group>

                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Drink consumption for the selected filters
                                                </Text>
                                            </div>
                                        </Group>

                                        <Button
                                            color="red"
                                            variant="light"
                                            leftSection={
                                                <IconDownload
                                                    size={
                                                        17
                                                    }
                                                />
                                            }
                                            onClick={
                                                exportToExcel
                                            }
                                        >
                                            Export Excel
                                        </Button>
                                    </Group>

                                    <Divider />

                                    <ScrollArea>
                                        <Table
                                            verticalSpacing="sm"
                                            horizontalSpacing="lg"
                                            highlightOnHover
                                        >
                                            <Table.Thead>
                                                <Table.Tr>
                                                    <Table.Th>
                                                        Name
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Collection
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Date
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Bar
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        Beer
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        Water
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        Wine
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        Total
                                                    </Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>

                                            <Table.Tbody>
                                                {filteredOverview.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => (
                                                        <Table.Tr
                                                            key={`${row.name}-${row.date}-${row.bar}-${index}`}
                                                        >
                                                            <Table.Td>
                                                                <Text
                                                                    fw={
                                                                        600
                                                                    }
                                                                    size="sm"
                                                                >
                                                                    {
                                                                        row.name
                                                                    }
                                                                </Text>
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {row.collectionName ? (
                                                                    <Badge
                                                                        color="gray"
                                                                        variant="light"
                                                                    >
                                                                        {
                                                                            row.collectionName
                                                                        }
                                                                    </Badge>
                                                                ) : (
                                                                    <Text
                                                                        c="dimmed"
                                                                        size="sm"
                                                                    >
                                                                        -
                                                                    </Text>
                                                                )}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                <Text size="sm">
                                                                    {formatDisplayDate(
                                                                        row.date
                                                                    )}
                                                                </Text>
                                                            </Table.Td>

                                                            <Table.Td>
                                                                <Badge
                                                                    color="red"
                                                                    variant="light"
                                                                >
                                                                    {getBarName(
                                                                        row.bar
                                                                    )}
                                                                </Badge>
                                                            </Table.Td>

                                                            <Table.Td ta="center">
                                                                {
                                                                    row.beer
                                                                }
                                                            </Table.Td>

                                                            <Table.Td ta="center">
                                                                {
                                                                    row.water
                                                                }
                                                            </Table.Td>

                                                            <Table.Td ta="center">
                                                                {
                                                                    row.wine
                                                                }
                                                            </Table.Td>

                                                            <Table.Td ta="center">
                                                                <Text
                                                                    fw={
                                                                        800
                                                                    }
                                                                >
                                                                    {
                                                                        row.total
                                                                    }
                                                                </Text>
                                                            </Table.Td>
                                                        </Table.Tr>
                                                    )
                                                )}
                                            </Table.Tbody>

                                            {/* TOTAL */}

                                            <Table.Tfoot>
                                                <Table.Tr
                                                    style={{
                                                        background:
                                                            "var(--mantine-color-gray-0)",
                                                    }}
                                                >
                                                    <Table.Th>
                                                        TOTAL
                                                    </Table.Th>

                                                    <Table.Th />

                                                    <Table.Th />

                                                    <Table.Th />

                                                    <Table.Th ta="center">
                                                        {
                                                            totals.beer
                                                        }
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        {
                                                            totals.water
                                                        }
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        {
                                                            totals.wine
                                                        }
                                                    </Table.Th>

                                                    <Table.Th ta="center">
                                                        <Text
                                                            fw={
                                                                800
                                                            }
                                                            c="red"
                                                        >
                                                            {
                                                                totals.total
                                                            }
                                                        </Text>
                                                    </Table.Th>
                                                </Table.Tr>
                                            </Table.Tfoot>
                                        </Table>
                                    </ScrollArea>
                                </Paper>
                            </>
                        )}

                    {/* ===================================== */}
                    {/* EMPTY */}
                    {/* ===================================== */}

                    {!loading &&
                        eventId >
                        0 &&
                        filteredOverview.length ===
                        0 && (
                            <Paper
                                withBorder
                                radius="lg"
                                p={60}
                            >
                                <Stack
                                    align="center"
                                    gap="xs"
                                >
                                    <ThemeIcon
                                        color="gray"
                                        variant="light"
                                        size={
                                            54
                                        }
                                        radius="xl"
                                    >
                                        <IconReceipt
                                            size={
                                                24
                                            }
                                        />
                                    </ThemeIcon>

                                    <Text
                                        fw={
                                            600
                                        }
                                    >
                                        No drinks found
                                    </Text>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                        ta="center"
                                    >
                                        No drink consumption matches the selected filters.
                                    </Text>

                                    {activeFilterCount >
                                        0 && (
                                            <Button
                                                mt="sm"
                                                variant="light"
                                                color="red"
                                                leftSection={
                                                    <IconRefresh
                                                        size={
                                                            16
                                                        }
                                                    />
                                                }
                                                onClick={
                                                    resetFilters
                                                }
                                            >
                                                Reset filters
                                            </Button>
                                        )}
                                </Stack>
                            </Paper>
                        )}
                </Stack>
            </Container>
        </div>
    );
};

export default AdminDrinkOverview;